import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Input, Alert } from "@repo/ui";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { getWithdrawSteps } from "../../constants/steps";
import { gsap } from "gsap";
import {
  CreateWithdrawResponse,
  validateWithdrawMFA,
  ValidateWithdrawMFARequest,
} from "../../../../services/withdrawApiService";
import BackButton from "../../../../components/BackButton";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";

const LOCALE = "modules_MyGiftTable_pages_WithdrawPage_WithdrawPageStep4";

const WithdrawPageStep4: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const steps = getWithdrawSteps(t);

  const navigate = useNavigate();
  const [mfaCode, setMfaCode] = useState<string>("");
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);

  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  const handleContinue = async () => {
    // Validate MFA Code
    if (!mfaCode || mfaCode.length !== 6) {
      setAlert({
        message: t("mfaCodeInvalidError"),
        level: "warn",
      });
      return;
    }

    // Retrieve the full withdrawal object from localStorage
    const storedWithdrawal = localStorage.getItem("withdrawal");
    if (!storedWithdrawal) {
      setAlert({
        message: t("withdrawIDMissingError"),
        level: "error",
      });
      return;
    }

    const withdrawal: CreateWithdrawResponse = JSON.parse(storedWithdrawal);

    try {
      // Prepare the ValidateWithdrawMFARequest with optional IDs
      const mfaRequest: ValidateWithdrawMFARequest = {
        withdrawID: withdrawal.withdrawalID || undefined,
        productExchangeID: withdrawal.productExchangeID || undefined,
        mfaCode,
      };

      // Call the validateWithdrawMFA service
      await validateWithdrawMFA(mfaRequest);

      console.log("MFA code validated successfully:", mfaCode);

      // Navigate to the next step upon success
      navigate("/my-table/withdraw/step5");
    } catch (error) {
      console.error("Error validating MFA code:", error);
      setAlert({
        message: t("submitMfaCodeError"),
        level: "error",
      });
    }
  };

  const handleBack = () => {
    navigate("/my-table/withdraw/step3");
  };

  return (
    <PageContainer>
      <div className="mt-16"></div>
      <StepIndicator steps={steps} currentStep={3} progressPercentage={75} />
      <AnimatedCard className="bg-white max-w-lg w-full">
        <BackButton text={t("backButtonText")} onClick={handleBack} />

        <h2 className="text-2xl font-bold mb-4 text-primary">
          {t("identityConfirmation")}
        </h2>
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}
        <div className="flex flex-col space-y-4">
          <p className="text-gray-600">{t("mfaInstructions")}</p>

          <label htmlFor="mfaCode" className="text-label font-paragraph">
            {t("enterCodeLabel")}
          </label>
          <Input
            placeholder={t("mfaPlaceholder")}
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
          />

          <Button
            label={t("continueButton")}
            onClick={handleContinue}
            type="primary"
            className="w-full mt-6"
          />
        </div>
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(WithdrawPageStep4));
